import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { createFormGroupState, FormGroupState } from 'ngrx-forms';

import {
  SelectionOptionColors,
  SelectionOptionInstallerCrews,
  SelectionOptionProducts,
  SelectionOptionProfiles,
  SelectionOptionSplashes,
  SelectionOptionThicknesses,
} from '@surface-elements/shared/shared-ui';

// COLORS
export interface SelectionColorFormValue {
  _id: string;
  product: string;
  color: string;
}

export const COLOR_FORM_ID = 'colorForm';

export const initialColorFormState =
  createFormGroupState<SelectionColorFormValue>(COLOR_FORM_ID, {
    _id: null,
    product: '',
    color: '',
  });

export interface ColorState extends EntityState<SelectionOptionColors> {
  error: string;
}

export const selectionColorAdapter: EntityAdapter<SelectionOptionColors> =
  createEntityAdapter<SelectionOptionColors>({
    selectId: (colorSelectionOptions: SelectionOptionColors) =>
      colorSelectionOptions._id,
  });

export const defaultSelectionOptionColors = {
  error: '',
};

export const selectionColorInitialState: ColorState = selectionColorAdapter.getInitialState(
  defaultSelectionOptionColors
);

// PRODUCTS
export interface SelectionProductFormValue {
  _id: string;
  product: string;
}

export const PRODUCT_FORM_ID = 'productForm';

export const initialProductFormState =
  createFormGroupState<SelectionProductFormValue>(PRODUCT_FORM_ID, {
    _id: null,
    product: '',
  });

interface ProductsState extends EntityState<SelectionOptionProducts> {
  error: string;
}

export const selectionProductAdapter: EntityAdapter<SelectionOptionProducts> =
  createEntityAdapter<SelectionOptionProducts>({
    selectId: (productSelectionOptions: SelectionOptionProducts) =>
      productSelectionOptions._id,
  });

export const defaultSelectionOptionProducts = {
  error: '',
};

export const selectionProductInitialState: ProductsState =
  selectionProductAdapter.getInitialState(defaultSelectionOptionProducts);

// PROFILES
export interface SelectionProfileFormValue {
  _id: string;
  profile: string;
}

export const PROFILE_FORM_ID = 'profileForm';

export const initialProfileFormState =
  createFormGroupState<SelectionProfileFormValue>(PROFILE_FORM_ID, {
    _id: null,
    profile: '',
  });

interface ProfileState extends EntityState<SelectionOptionProfiles> {
  error: string;
}

export const selectionProfileAdapter: EntityAdapter<SelectionOptionProfiles> =
  createEntityAdapter<SelectionOptionProfiles>({
    selectId: (profileSelectionOptions: SelectionOptionProfiles) =>
      profileSelectionOptions._id,
  });

export const defaultSelectionOptionProfiles = {
  error: '',
};

export const selectionProfileInitialState: ProfileState =
  selectionProfileAdapter.getInitialState(defaultSelectionOptionProfiles);

// SPLASHES
export interface SelectionSplashFormValue {
  _id: string;
  splash: string;
}

export const SPLASH_FORM_ID = 'splashForm';

export const initialSplashFormState =
  createFormGroupState<SelectionSplashFormValue>(SPLASH_FORM_ID, {
    _id: null,
    splash: '',
  });

interface SplashState extends EntityState<SelectionOptionSplashes> {
  error: string;
}

export const selectionSplashAdapter: EntityAdapter<SelectionOptionSplashes> =
  createEntityAdapter<SelectionOptionSplashes>({
    selectId: (splashSelectionOptions: SelectionOptionSplashes) =>
      splashSelectionOptions._id,
  });

export const defaultSelectionOptionSplashes = {
  error: '',
};

export const selectionSplashInitialState: SplashState =
  selectionSplashAdapter.getInitialState(defaultSelectionOptionSplashes);

// THICKNESSES
export interface SelectionThicknessFormValue {
  _id: string;
  thickness: string;
}

export const THICKNESS_FORM_ID = 'thicknessForm';

export const initialThicknessFormState =
  createFormGroupState<SelectionThicknessFormValue>(THICKNESS_FORM_ID, {
    _id: null,
    thickness: '',
  });

interface ThicknessState extends EntityState<SelectionOptionThicknesses> {
  error: string;
}

export const selectionThicknessAdapter: EntityAdapter<SelectionOptionThicknesses> =
  createEntityAdapter<SelectionOptionThicknesses>({
    selectId: (thicknessSelectionOptions: SelectionOptionThicknesses) =>
      thicknessSelectionOptions._id,
  });

export const defaultSelectionOptionThicknesses = {
  error: '',
};

export const selectionThicknessInitialState: ThicknessState =
  selectionThicknessAdapter.getInitialState(defaultSelectionOptionThicknesses);

// INSTALLER CREWS
export interface SelectionInstallerCrewFormValue {
  _id: string;
  crew: string;
}

export const INSTALLER_CREW_FORM_ID = 'installerCrewForm';

export const initialInstallerCrewFormState =
  createFormGroupState<SelectionInstallerCrewFormValue>(
    INSTALLER_CREW_FORM_ID,
    {
      _id: null,
      crew: '',
    }
  );

interface InstallerCrewState
  extends EntityState<SelectionOptionInstallerCrews> {
  error: string;
}

export const selectionInstallerCrewAdapter: EntityAdapter<SelectionOptionInstallerCrews> =
  createEntityAdapter<SelectionOptionInstallerCrews>({
    selectId: (installerCrewSelectionOptions: SelectionOptionInstallerCrews) =>
      installerCrewSelectionOptions._id,
  });

export const defaultSelectionOptionInstallerCrews = {
  error: '',
};

export const selectionInstallerCrewInitialState: InstallerCrewState =
  selectionInstallerCrewAdapter.getInitialState(
    defaultSelectionOptionInstallerCrews
  );

export interface State {
  selectionOptions: string[];
  colors: ColorState;
  colorForm: FormGroupState<SelectionColorFormValue>;
  products: ProductsState;
  productForm: FormGroupState<SelectionProductFormValue>;
  profiles: ProfileState;
  profileForm: FormGroupState<SelectionProfileFormValue>;
  splashes: SplashState;
  splashForm: FormGroupState<SelectionSplashFormValue>;
  thicknesses: ThicknessState;
  thicknessForm: FormGroupState<SelectionThicknessFormValue>;
  installerCrews: InstallerCrewState;
  installerCrewForm: FormGroupState<SelectionInstallerCrewFormValue>;
  error: string;
}

export const initialState: State = {
  selectionOptions: [],
  colors: selectionColorInitialState,
  colorForm: initialColorFormState,
  products: selectionProductInitialState,
  productForm: initialProductFormState,
  profiles: selectionProfileInitialState,
  profileForm: initialProfileFormState,
  splashes: selectionSplashInitialState,
  splashForm: initialSplashFormState,
  thicknesses: selectionThicknessInitialState,
  thicknessForm: initialThicknessFormState,
  installerCrews: selectionInstallerCrewInitialState,
  installerCrewForm: initialInstallerCrewFormState,
  error: '',
};
